import {useEffect, useState} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {DataTable} from "../../components/data-table";
import {SiteHeader} from "../../components/site-header";
import {BinUsageStats} from "./bin-usage-stats";
import {Button} from "@/components/ui/button";
import {useFilters} from "@/common/Components/SideBar/Layout";
import axiosInstance from "@/api/axios";
import {HeatMap} from "@/components";
import {WasteComposition} from "@/components/waste-composition";
import {BinMediaViewer} from "@/components/bin-images-viewer";
import {DashboardExport} from "@/components/DashboardExport";
import {useTranslation} from 'react-i18next';
import {
    WasteStreamProduction,
    Stats,
    BinUsageRanking,
    FillLevelConsistency,
    PeakUsageTime,
    PeakFillLevel,
} from "../../components";
import SubclassDisplay from "../../components/Subclass-Display/SubclassDisplay";
import ContaminationChart from "../../components/ContaminationChart";
import WasteCreated from "@/pages/Others/Components/WasteCreated";
import {initializeApp} from '@firebase/app';
import {getMessaging, getToken, onMessage} from '@firebase/messaging';
import SubclassCardView from "../../components/Subclass-Display/SubclassCardView";

const firebaseConfig = {
    apiKey: "AIzaSyBylDQErfG_E2F7LpO6boLYf4QMRwtek3g",
    authDomain: "plaex-notifications-8dfa0.firebaseapp.com",
    projectId: "plaex-notifications-8dfa0",
    storageBucket: "plaex-notifications-8dfa0.firebasestorage.app",
    messagingSenderId: "16520628768",
    appId: "1:16520628768:web:f3f43ec41ebaa3ba8f8a45"
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

const STORAGE_KEY_BINS = "plaex_dashboard_selected_bins";

export default function Page() {
    // State variables
    const [userRoleState, setUserRoleState] = useState(null);
    const [isLoadingRole, setIsLoadingRole] = useState(true);
    const [hasBinImages, setHasBinImages] = useState(false);
    const [binUsageIds, setBinUsageIds] = useState(null);
    const [showBinUsageStats, setShowBinUsageStats] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [refreshKey, setRefreshKey] = useState(0);
    const [localTimeRangeDropdownOpen, setLocalTimeRangeDropdownOpen] = useState(false);
    const [localBinDropdownOpen, setLocalBinDropdownOpen] = useState(false);
    const [localSelectedTimeRange, setLocalSelectedTimeRange] = useState("Year");
    const [localSelectedBins, setLocalSelectedBins] = useState([]);
    const [localAllBins, setLocalAllBins] = useState([]);
    const [localWasteTypeDropdownOpen, setLocalWasteTypeDropdownOpen] = useState(false);
    const [localSelectedWasteTypes, setLocalSelectedWasteTypes] = useState(['organic']);
    const [messages, setMessages] = useState([]);
    const [wasteCreatedKg, setWasteCreatedKg] = useState(0);

    // New state for bin type tracking
    const [binTypes, setBinTypes] = useState({});
    const [hasGarbyVisionBins, setHasGarbyVisionBins] = useState(false);

    const {t} = useTranslation();

    // Hooks
    const location = useLocation();
    const navigate = useNavigate();

    // Context values - GET CUSTOM DATE RANGE FROM CONTEXT
    const {
        selectedTimeRange: contextTimeRange,
        selectedBins: contextBins,
        userRole,
        selectedWasteTypes: contextWasteTypes,
        wasteTypeOptions: contextWasteTypeOptions,
        customDateRange: contextCustomDateRange,
        isCustomDateRange: contextIsCustomDateRange,
    } = useFilters();

    // Derived values
    const selectedBins = contextBins || localSelectedBins;
    const selectedTimeRange = contextTimeRange || localSelectedTimeRange;
    const timeRangeOptions = ["Day", "Week", "Month", "3 Month", "6 Month", "Year"];
    const selectedWasteTypes = contextWasteTypes || localSelectedWasteTypes;
    const wasteTypeOptions = contextWasteTypeOptions || ['pmd', 'organic', 'paper', 'residual'];
    
    // Use custom date range from context if available
    const customDateRange = contextCustomDateRange || [{
        startDate: null,
        endDate: null,
        key: 'selection'
    }];
    const isCustomDateRange = contextIsCustomDateRange || false;

    // Function to fetch bin types and determine if Garby Vision bins are present
    const fetchBinTypes = async (bins) => {
        if (!bins || bins.length === 0) {
            setHasGarbyVisionBins(false);
            setBinTypes({});
            return;
        }

        try {
            const response = await axiosInstance.get('/get_bins');
            const allBins = response.data;

            const binTypeMap = {};
            let garbyVisionFound = false;

            bins.forEach(binId => {
                const bin = allBins.find(b => b.unique_identifier === binId);
                if (bin) {
                    binTypeMap[binId] = {
                        isGarbyVision: bin.is_garby_vision,
                        isGarfill: bin.is_garfill,
                        isWeightScale: bin.is_weight_scale
                    };

                    // Check if this is a Garby Vision bin
                    if (bin.is_garby_vision) {
                        garbyVisionFound = true;
                    }
                }
            });

            setBinTypes(binTypeMap);
            setHasGarbyVisionBins(garbyVisionFound);

        } catch (error) {
            console.error("Error fetching bin types:", error);
            setHasGarbyVisionBins(false);
            setBinTypes({});
        }
    };

    // Check bin types when selected bins change
    useEffect(() => {
        fetchBinTypes(selectedBins);
    }, [selectedBins]);

    // Initialize user role from localStorage
    useEffect(() => {
        const savedRole = localStorage.getItem("userRole");
        if (savedRole) {
            setUserRoleState(savedRole);
            setIsLoadingRole(false);
        }
    }, []);

    // Push notifications initialization
    useEffect(() => {
        let messaging = null;
        let unsubscribe = null;

        const initializeNotifications = async () => {
            try {
                if (!('serviceWorker' in navigator) || !('Notification' in window)) {
                    console.log('Service Worker or Notifications not supported');
                    return;
                }

                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    console.log('Notification permission denied');
                    return;
                }

                const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                messaging = getMessaging(app);

                const currentToken = await getToken(messaging, {
                    serviceWorkerRegistration: swRegistration
                });

                if (currentToken) {
                    console.log('Registration token:', currentToken);
                    await sendTokenToServer(currentToken);
                }
            } catch (error) {
                console.error('Error initializing notifications:', error);
            }
        };

        initializeNotifications();

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [navigate]);

    const sendTokenToServer = async (token) => {
        try {
            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken = csrfResponse.data.csrfToken;

            const response = await axiosInstance.post('/fcm/devices/', {
                registration_id: token,
                type: 'web'
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                withCredentials: true,
            });

            if (response.status === 200 || response.status === 201) {
                console.log('Token sent to server successfully');
            } else {
                console.error('Failed to send token to server');
            }
        } catch (error) {
            console.error('Error sending token to server:', error);
        }
    };

    // Fetch user role if not provided by context
    useEffect(() => {
        if (userRole) {
            setUserRoleState(userRole);
            localStorage.setItem("userRole", userRole);
            setIsLoadingRole(false);
        } else if (!userRoleState) {
            const fetchUserRole = async () => {
                try {
                    const response = await axiosInstance.get("usertier");
                    const fetchedRole = response.data;
                    setUserRoleState(fetchedRole);
                    localStorage.setItem("userRole", fetchedRole);
                } catch (error) {
                    console.error("Error fetching user role:", error);
                } finally {
                    setIsLoadingRole(false);
                }
            };
            fetchUserRole();
        }
    }, [userRole, userRoleState]);

    // Check for bin images when selected bins change
    useEffect(() => {
        const checkForBinImages = async () => {
            if (selectedBins.length === 1) {
                try {
                    const response = await axiosInstance.get(`/bin-media/?bin_id=${selectedBins[0]}`);
                    setHasBinImages(response.data.length > 0);
                } catch (error) {
                    console.error("Error checking for bin images:", error);
                    setHasBinImages(false);
                }
            } else {
                setHasBinImages(false);
            }
        };

        checkForBinImages();
    }, [selectedBins]);

    // Fetch bin IDs when using local state - UPDATED WITH LOCAL STORAGE
    useEffect(() => {
        if (!contextBins) {
            const fetchBinIds = async () => {
                try {
                    // Use the new endpoint that includes status
                    const response = await axiosInstance.get("bins-with-status");
                    const binsWithStatus = response.data || [];

                    // Extract just the IDs for compatibility with existing logic
                    const binIds = binsWithStatus.map(bin => bin.id);
                    const validBinIds = binIds.filter((id) => id && typeof id === "string");

                    // CHECK LOCAL STORAGE FOR SAVED SELECTION
                    const savedSelection = localStorage.getItem(STORAGE_KEY_BINS);
                    let initialSelection = [];

                    if (savedSelection) {
                        try {
                            const parsedSelection = JSON.parse(savedSelection);
                            // Only keep bins that actually exist in the current fetch
                            initialSelection = parsedSelection.filter(id => validBinIds.includes(id));
                        } catch (e) {
                            console.error("Error parsing saved bin selection", e);
                        }
                    }

                    // Fallback to top 3 if no valid saved selection
                    if (initialSelection.length === 0) {
                        initialSelection = validBinIds.slice(0, Math.min(3, validBinIds.length));
                    }

                    // Set both the enhanced bins and the simple bin list
                    setLocalAllBins(binsWithStatus); // Now contains status info
                    setLocalSelectedBins(initialSelection);
                } catch (error) {
                    console.error("Error fetching bin IDs:", error);
                    // Fallback to original endpoint if new one fails
                    try {
                        const fallbackResponse = await axiosInstance.get("bin_ids");
                        const binIds = fallbackResponse.data || [];
                        const validBinIds = binIds.filter((id) => id && typeof id === "string");
                        setLocalAllBins(validBinIds.map(id => ({id, status: 'offline'})));
                        setLocalSelectedBins(validBinIds.slice(0, Math.min(3, validBinIds.length)));
                    } catch (fallbackError) {
                        console.error("Error with fallback bin fetch:", fallbackError);
                    }
                }
            };

            fetchBinIds();
        }
    }, [contextBins]);

    // NEW: Save selection to LocalStorage whenever it changes
    useEffect(() => {
        if (!contextBins && localSelectedBins) {
            // We ensure localAllBins is populated before saving to avoid overwriting with empty array on initial mount before fetch completes
            if (localAllBins.length > 0) {
                localStorage.setItem(STORAGE_KEY_BINS, JSON.stringify(localSelectedBins));
            }
        }
    }, [localSelectedBins, contextBins, localAllBins]);

    // Bin usage stats
    useEffect(() => {
        const fetchBinUsageIds = async () => {
            try {
                const queryParams = selectedBins.length > 0
                    ? `?bins=${selectedBins.join(',')}`
                    : '';
                const response = await axiosInstance.get(`bin_usage_ids${queryParams}`, {
                    withCredentials: true,
                });
                const nonGarbyVisionBins = response.data || [];
                setBinUsageIds(nonGarbyVisionBins);
                setShowBinUsageStats(nonGarbyVisionBins.length > 0);
            } catch (error) {
                console.error("Error fetching bin IDs:", error);
                setShowBinUsageStats(false);
                setBinUsageIds([]);
            }
        };

        if (selectedBins.length > 0) {
            fetchBinUsageIds();
        } else {
            setShowBinUsageStats(false);
            setBinUsageIds([]);
        }
    }, [selectedBins]);


    useEffect(() => {
        const refreshBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];
                setLocalAllBins(binsWithStatus);
            } catch (error) {
                console.error("Error refreshing bin statuses:", error);
            }
        };

        // Refresh statuses every minute
        const interval = setInterval(refreshBinStatuses, 60000);

        return () => clearInterval(interval);
    }, []);

    const selectLocalTimeRange = (timeRange) => {
        setLocalSelectedTimeRange(timeRange);
        return false;
    };

    const toggleLocalBinSelection = (bin) => {
        if (localSelectedBins.includes(bin)) {
            setLocalSelectedBins(localSelectedBins.filter((item) => item !== bin));
        } else {
            setLocalSelectedBins([...localSelectedBins, bin]);
        }
        return false;
    };

    const toggleLocalWasteTypeSelection = (wasteType) => {
        if (localSelectedWasteTypes.includes(wasteType)) {
            setLocalSelectedWasteTypes(localSelectedWasteTypes.filter((item) => item !== wasteType));
        } else {
            setLocalSelectedWasteTypes([...localSelectedWasteTypes, wasteType]);
        }
        return false;
    };

    const handleUploadSuccess = () => {
        setRefreshKey((prev) => prev + 1);
    };

    const currentWasteStream = selectedWasteTypes.length > 0
        ? selectedWasteTypes[0]
        : 'organic';

    const getStartDate = (timeRange) => {
        const now = new Date();
        switch (timeRange.toLowerCase()) {
            case 'day':
                return new Date(now.setDate(now.getDate() - 1));
            case 'week':
                return new Date(now.setDate(now.getDate() - 7));
            case 'month':
                return new Date(now.setMonth(now.getMonth() - 1));
            case '3month':
                return new Date(now.setMonth(now.getMonth() - 3));
            case '6month':
                return new Date(now.setMonth(now.getMonth() - 6));
            case 'year':
                return new Date(now.setFullYear(now.getFullYear() - 1));
            default:
                return new Date(now.setDate(now.getDate() - 1));
        }
    };

    return (
        <DashboardExport>
            <div className="export-wrapper">
                <div className="tab-content h-full">
                    <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">

                        {!contextTimeRange && (
                            <div
                                className="sticky top-16 pt-4 pb-6 text-right z-10 w-full ml-auto px-4 sm:pr-0 bg-gradient-to-t from-white/0 to-white dark:from-slate-950/0 dark:to-slate-950 flex gap-2 justify-end">
                                <SiteHeader
                                    timeRangeDropdownOpen={localTimeRangeDropdownOpen}
                                    setTimeRangeDropdownOpen={setLocalTimeRangeDropdownOpen}
                                    binDropdownOpen={localBinDropdownOpen}
                                    setBinDropdownOpen={setLocalBinDropdownOpen}
                                    selectedTimeRange={localSelectedTimeRange}
                                    selectTimeRange={selectLocalTimeRange}
                                    timeRangeOptions={timeRangeOptions}
                                    selectedBins={localSelectedBins}
                                    allBins={localAllBins}
                                    toggleBinSelection={toggleLocalBinSelection}
                                    wasteTypeDropdownOpen={localWasteTypeDropdownOpen}
                                    setWasteTypeDropdownOpen={setLocalWasteTypeDropdownOpen}
                                    selectedWasteTypes={localSelectedWasteTypes}
                                    toggleWasteTypeSelection={toggleLocalWasteTypeSelection}
                                    wasteTypeOptions={wasteTypeOptions}
                                />
                            </div>
                        )}

                        <div
                            className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-5 gap-6 sm:gap-8 sm:mt-4 font-sans">
                            <div className="mb-6 sm:mb-0 flex flex-col gap-8">
                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                    <div className="aspect-[16/10]">
                                        <div className="relative w-full overflow-auto">
                                            <DataTable
                                                className="w-full caption-bottom text-sm aspect-[16/10]"
                                                title={t("filllevellabel")}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                   <WasteCreated 
                                            bins={selectedBins} 
                                            timeRange={selectedTimeRange}
                                            onKgWasteUpdate={setWasteCreatedKg}
                                            customDateRange={customDateRange}      
                                            isCustomDateRange={isCustomDateRange}  
                                    />
                                </div>

                                {/* Contamination Chart - only show if at least one Garby Vision bin is selected */}
                                {selectedBins.length > 0 && hasGarbyVisionBins && (
                                    <div className="sm:rounded-lg border-t sm:border shadow-sm bg-muted/50">
                                            <ContaminationChart
                                                title={t('contamination.title')}
                                                timeRange={
                                                    selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "3month"
                                                        ? "3months"
                                                        : selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "6month"
                                                            ? "6months"
                                                            : selectedTimeRange.toLowerCase().replace(/\s+/g, "")
                                                }
                                                bins={selectedBins}
                                                customDateRange={customDateRange}
                                                isCustomDateRange={isCustomDateRange}
                                            />
                                    </div>
                                )}
                            </div>

                            <div
                                className="grid grid-cols-subgrid lg:col-span-2 2xl:col-span-4 content-start gap-1 sm:gap-4 items-start">
                                <Stats
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastegenerationlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />
                                
                                {selectedBins.length > 0 && (
                                    <SubclassDisplay
                                        className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                        id={selectedBins}
                                        currentWasteItem={selectedWasteTypes[0] || 'organic'}
                                        currentWasteItem_={selectedWasteTypes[0] || 'organic'}
                                        dayvalue={selectedTimeRange.toLowerCase()}
                                        selectedButton={selectedTimeRange.toLowerCase()}
                                        datevalue={customDateRange}
                                        wasteweight={wasteCreatedKg}
                                        isCustomDateRange={isCustomDateRange}
                                    />
                                )}

                          {selectedBins.length > 0 && (
    <div 
        // FIX: 'aspect-[16/22]' forces the height to match 2 stacked cards + gap.
        // 'overflow-hidden' ensures the content doesn't push the grid open.
        className="lg:col-span-2 lg:row-span-2 aspect-[16/22] sm:rounded-lg bg-muted/50 overflow-hidden flex flex-col"
    >
        <SubclassCardView
            className="w-full h-full" // Ensure h-full is passed
            id={selectedBins}
            dayvalue={selectedTimeRange.toLowerCase()}
            datevalue={customDateRange}
            isCustomDateRange={isCustomDateRange}
            wasteweight={wasteCreatedKg}
            totalwasteproduceddata={1004}
        />
    </div>
)}
                                {selectedBins.length > 0 && (
                                    <BinMediaViewer
                                        key={refreshKey}
                                        selectedBin={selectedBins}
                                        className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    />
                                )}

                                <WasteStreamProduction
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastestreamproductionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <PeakUsageTime
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("peakUsageTime")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <FillLevelConsistency
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("fillLevelConsistency.title")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <BinUsageRanking
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("binUsageRanking.defaultTitle")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <WasteComposition
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastecompositionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardExport>
    );
}